/* eslint-disable sort-imports */
/**
 * API Client Utilities
 * ============================================================================
 *
 * Purpose:
 * --------
 * Provides shared client-side utilities used by the API layer for
 * request tracing and authentication token management.
 *
 * Design principles:
 * ------------------
 * - These utilities are stateless helpers with no UI dependencies.
 * - Error handling delegated to core/error-handling layer.
 * - Local storage access is intentionally abstracted behind helpers.
 *
 * Non-goals:
 * ----------
 * - Making network requests
 * - Managing application state
 * - Error handling (handled by core/error-handling)
 * - Rendering UI or triggering side effects directly
 *
 * Changes to this file affect request tracing and authentication flows.
 * ============================================================================
 */

import type { AxiosError, AxiosResponse } from 'axios';
import { STORAGE_KEYS, ERROR_CODES, ERROR_MESSAGE_KEYS, ERROR_CODE_MAP, TITLE_KEYS } from './constants';
import type { ApiError } from './types';
import i18n from '@/locales';
import { showNotification, NOTIFICATION_TYPE } from '@/core/notifications';

/**
 * Generates a unique request identifier for tracing.
 *
 * Used by request interceptors to correlate client-side actions
 * with backend logs and monitoring systems.
 */
export const generateRequestId = (): string =>
  `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

/**
 * Retrieves the current access token from persistent storage.
 *
 * Checks both localStorage (remember me) and sessionStorage (session only).
 * Returns null when the user is unauthenticated or the token
 * has been explicitly cleared.
 */
export const getAuthToken = (): string | null => {
  // Check localStorage first (remember me), then sessionStorage
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    || sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Retrieves the refresh token from persistent storage.
 *
 * Checks both localStorage (remember me) and sessionStorage (session only).
 * This token is used exclusively for session renewal flows.
 */
export const getRefreshToken = (): string | null => {
  // Check localStorage first (remember me), then sessionStorage
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    || sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Persists authentication tokens after successful login
 * or token refresh.
 *
 * Token storage is centralized here to ensure consistent
 * key usage and future extensibility.
 * Respects "remember me" setting from auth layer.
 */
export const setAuthTokens = (accessToken: string, refreshToken: string): void => {
  // Check if user selected "remember me" by checking if tokens are in localStorage
  const useLocalStorage = localStorage.getItem('bimanova_remember_me') === 'true'
    || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) !== null;

  const storage = useLocalStorage ? localStorage : sessionStorage;
  storage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
  storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
};

/**
 * Clears all authentication-related data from storage.
 *
 * Called on explicit logout and when session recovery fails
 * to guarantee a clean authentication state.
 * Clears from both localStorage and sessionStorage to be thorough.
 */
export const clearAuthTokens = (): void => {
  // Clear from both storages to ensure complete cleanup
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Resolves the frontend error code for a failed request.
 *
 * Backend-provided error codes take precedence when available.
 * HTTP status codes are used as a fallback to ensure a stable
 * error contract for the UI.
 */
export const getErrorCodeFromStatus = (status: number, backendCode?: string): string => {
  if (backendCode) return backendCode;

  return ERROR_CODE_MAP[status] ?? ERROR_CODES.UNKNOWN_ERROR;
};

/**
 * Determines the notification severity for a given error.
 *
 * Validation and client errors are treated as warnings,
 * while server-side failures are surfaced as errors.
 */
export const getNotificationType = (errorCode: string, status: number): 'error' | 'warning' => {
  if (errorCode === ERROR_CODES.VALIDATION_ERROR) return 'warning';
  if (status >= 400 && status < 500) return 'warning';
  return 'error';
};

/**
 * Resolves translated error message for given error code
 *
 * Uses translation keys from constants and fallback to default message.
 */
const getErrorMessage = (errorCode: string): string => {
  const key = ERROR_MESSAGE_KEYS[errorCode as keyof typeof ERROR_MESSAGE_KEYS];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return key ? String(i18n.t(key as any)) : String(i18n.t('api-errors:messages.default' as any));
};

/**
 * Resolves a short, user-facing title for error notifications.
 *
 * Titles are intentionally generic and should not expose
 * backend implementation details.
 */
export const getErrorTitle = (errorCode: string): string => {
  const key = TITLE_KEYS[errorCode];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return key ? String(i18n.t(key as any)) : String(i18n.t('api-errors:titles.default' as any));
};

/**
 * Determines how long an error notification should remain visible.
 *
 * More severe errors are displayed longer to increase visibility,
 * while validation issues remain brief to avoid user fatigue.
 */
export const getNotificationDuration = (errorCode: string, status: number): number => {
  if (status >= 500) return 7000;
  if (errorCode === ERROR_CODES.VALIDATION_ERROR) return 6000;
  return 5000;
};

/**
 * Transform error to standard ApiError format
 */
export const transformError = (error: AxiosError | Error): ApiError => {
  if ('isAxiosError' in error && error.isAxiosError) {
    const axiosError = error;

    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return createApiError(ERROR_CODES.TIMEOUT, getErrorMessage(ERROR_CODES.TIMEOUT));
      }
      if (axiosError.code === 'ERR_CANCELED') {
        return createApiError(ERROR_CODES.CANCELED, getErrorMessage(ERROR_CODES.CANCELED));
      }
      return createApiError(ERROR_CODES.NETWORK_ERROR, getErrorMessage(ERROR_CODES.NETWORK_ERROR));
    }

    const { status, data } = axiosError.response;
    const requestId = axiosError.config?.headers?.['X-Request-ID'] as string;

    const backendError = data as {
      message?: string;
      code?: string;
      field?: string;
      errors?: Array<{ field: string; message: string; code?: string }>;
    };

    const errorCode = getErrorCodeFromStatus(status, backendError?.code);

    return {
      message: backendError?.message ?? getErrorMessage(errorCode),
      code: errorCode,
      status,
      field: backendError?.field,
      errors: backendError?.errors,
      requestId,
      timestamp: new Date().toISOString(),
    };
  }

  return createApiError(
    ERROR_CODES.UNKNOWN_ERROR,
    error?.message ?? getErrorMessage(ERROR_CODES.UNKNOWN_ERROR)
  );
};

const createApiError = (code: string, message: string, status = 0): ApiError => ({
  message,
  code,
  status,
  timestamp: new Date().toISOString(),
});

/**
 * Show error notification to user
 *
 * Skips notifications for:
 * - Canceled requests
 * - 401 errors (handled by redirect)
 */
export const showErrorNotification = (error: ApiError): void => {
  if (error.code === ERROR_CODES.CANCELED) {
    return;
  }

  if (error.status === 401) {
    return;
  }

  showNotification(NOTIFICATION_TYPE.ERROR, error.message, {
    title: getErrorTitle(error.code),
    duration: getNotificationDuration(error.code, error.status),
  });
};

/**
 * Show success notification to user
 */
export const showSuccessNotification = (message: string): void => {
  showNotification(NOTIFICATION_TYPE.SUCCESS, message);
};

export const logErrorToMonitoring = (error: ApiError): void => {
  console.error('Error logged to monitoring:', error);
};

/**
 * Generic wrapper for API calls
 *
 * Handles optional success notifications.
 */
export const apiRequest = async <T = unknown>(
  requestFn: () => Promise<AxiosResponse<T>>,
  options?: {
    showSuccessNotification?: boolean;
    successMessage?: string;
    suppressErrorNotification?: boolean;
  }
): Promise<T> => {
  const response = await requestFn();

  if (options?.showSuccessNotification && options?.successMessage) {
    showSuccessNotification(options.successMessage);
  }

  return response.data;
};

/**
 * Wrapper for mutation operations
 *
 * Automatically shows success notifications.
 */
export const apiMutation = async <T = unknown>(
  requestFn: () => Promise<AxiosResponse<T>>,
  successMessage?: string
): Promise<T> =>
  apiRequest(requestFn, {
    showSuccessNotification: true,
    successMessage: successMessage ?? 'Operation completed successfully',
  });
